import { useState, useEffect, useMemo } from 'react';
import {
  SS27_LINES, type SS27Line, fetchObjetivos,
  type ObjetivoRow, type NecesidadRow,
} from '../api/objetivosApi.js';
import { useExportExcel, type AgentExportRow } from '../hooks/useExportExcel.js';
import logoUrl from '../assets/losan-logo.png';

const eurFmt = new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
const eurCompact = new Intl.NumberFormat('es-ES', { notation: 'compact', style: 'currency', currency: 'EUR', maximumFractionDigits: 1 });
const intFmt = new Intl.NumberFormat('es-ES', { maximumFractionDigits: 0 });

type Metric = 'facturacion' | 'clientes' | 'clientesNuevos';

interface LineCell { facturacion: number; clientes: number; clientesNuevos: number }
interface AgentData {
  agentId: string;
  agentName: string;
  byLine: Record<SS27Line, LineCell>;
  totalFact: number;
  totalCli: number;
  totalNew: number;
  necesidades: string;
}

const LINE_COLORS: Record<SS27Line, string> = {
  MAN: '#1e3a8a', WOMAN: '#be185d', TEEN: '#0d9488', KIDS: '#ca8a04', BABY: '#0891b2', NEWBORN: '#7c3aed',
};

const METRIC_TABS: { id: Metric; label: string; fmt: (n: number) => string }[] = [
  { id: 'facturacion', label: 'Facturación prevista', fmt: (n) => eurFmt.format(n) },
  { id: 'clientes', label: 'Nº clientes', fmt: (n) => intFmt.format(n) },
  { id: 'clientesNuevos', label: 'Clientes nuevos', fmt: (n) => intFmt.format(n) },
];

export function ObjetivosSS27Admin() {
  const [data, setData] = useState<AgentData[]>([]);
  const [metric, setMetric] = useState<Metric>('facturacion');
  const [lineFilter, setLineFilter] = useState<SS27Line | 'ALL'>('ALL');
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const { exportExcel, exporting } = useExportExcel();

  useEffect(() => {
    let alive = true;
    fetchObjetivos()
      .then((res) => { if (alive) { setData(groupByAgent(res.rows, res.necesidades)); setStatus('ready'); } })
      .catch(() => { if (alive) setStatus('error'); });
    return () => { alive = false; };
  }, []);

  const fmt = METRIC_TABS.find((m) => m.id === metric)!.fmt;

  // metricValue for an agent honoring the line filter
  const agentMetric = (a: AgentData, m: Metric): number =>
    lineFilter === 'ALL'
      ? (m === 'facturacion' ? a.totalFact : m === 'clientes' ? a.totalCli : a.totalNew)
      : a.byLine[lineFilter][m];

  // KPI banner totals (respecting line filter)
  const kpis = useMemo(() => {
    let fact = 0, cli = 0, nue = 0;
    for (const a of data) { fact += agentMetric(a, 'facturacion'); cli += agentMetric(a, 'clientes'); nue += agentMetric(a, 'clientesNuevos'); }
    return { fact, cli, nue, agentes: data.length };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, lineFilter]);

  // Facturación por línea (always all lines, ignores line filter — it IS the breakdown)
  const byLineRevenue = useMemo(() => {
    const out = SS27_LINES.map((l) => ({ line: l, value: data.reduce((s, a) => s + a.byLine[l][metric], 0) }));
    const max = Math.max(1, ...out.map((o) => o.value));
    return { rows: out.sort((a, b) => b.value - a.value), max };
  }, [data, metric]);

  // Top agentes by current metric + line filter
  const agentRanking = useMemo(() => {
    const out = data.map((a) => ({ name: a.agentName || a.agentId, value: agentMetric(a, metric) }))
      .filter((x) => x.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
    const max = Math.max(1, ...out.map((o) => o.value));
    return { rows: out, max };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, metric, lineFilter]);

  function handleExport() {
    const rows: AgentExportRow[] = data.map((a) => ({
      agentId: a.agentId, agentName: a.agentName, byLine: a.byLine,
      totalFact: a.totalFact, totalCli: a.totalCli, totalNew: a.totalNew, necesidades: a.necesidades,
    }));
    void exportExcel(rows, `objetivos-ss27-losan.xlsx`);
  }

  const colTotal = (l: SS27Line) => data.reduce((s, a) => s + a.byLine[l][metric], 0);

  return (
    <div className="app">
      <header className="header">
        <div>
          <img className="header-logo" src={logoUrl} alt="LOSAN" />
          <h1>Objetivos SS27 · Visión administrador</h1>
          <div className="agent">
            {status === 'ready' ? `${data.length} agentes han respondido el cuestionario` : 'Previsiones de campaña por agente'}
          </div>
        </div>
        <div className="header-controls">
          <button className="export-pdf-btn no-export" onClick={handleExport} disabled={exporting || data.length === 0}>
            {exporting ? 'Generando…' : '⬇ Excel'}
          </button>
        </div>
      </header>

      {status === 'loading' && <div className="section"><div className="loading">Cargando objetivos…</div></div>}
      {status === 'error' && <div className="section"><div className="error">No se pudieron cargar los objetivos.</div></div>}
      {status === 'ready' && data.length === 0 && (
        <div className="section"><div className="empty-hint">Todavía ningún agente ha respondido el cuestionario SS27.</div></div>
      )}

      {status === 'ready' && data.length > 0 && (
        <>
          {/* KPI banner */}
          <div className="kpi-grid">
            <KpiBanner label="Facturación prevista" value={eurFmt.format(kpis.fact)} accent="var(--accent)" />
            <KpiBanner label="Clientes previstos" value={intFmt.format(kpis.cli)} accent="var(--green)" />
            <KpiBanner label="Clientes nuevos / reactivados" value={intFmt.format(kpis.nue)} accent="#ca8a04" />
            <KpiBanner label="Agentes que han respondido" value={intFmt.format(kpis.agentes)} accent="#be185d" />
          </div>

          {/* Controls: metric toggle + line filter */}
          <div className="ss27-bi-controls no-export">
            <div className="metric-toggle" role="tablist" aria-label="Métrica">
              {METRIC_TABS.map((m) => (
                <button key={m.id} role="tab" aria-selected={metric === m.id}
                  className={metric === m.id ? 'active' : ''} onClick={() => setMetric(m.id)}>
                  {m.label}
                </button>
              ))}
            </div>
            <label className="filter-dropdown">
              <span>Línea</span>
              <select value={lineFilter} onChange={(e) => setLineFilter(e.target.value as SS27Line | 'ALL')}>
                <option value="ALL">Todas las líneas</option>
                {SS27_LINES.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </label>
          </div>

          {/* Visualizations */}
          <div className="ss27-charts">
            <div className="section">
              <h2>{METRIC_TABS.find((m) => m.id === metric)!.label} por línea</h2>
              <BarChart rows={byLineRevenue.rows.map((r) => ({ label: r.line, value: r.value, color: LINE_COLORS[r.line] }))}
                max={byLineRevenue.max} fmt={metric === 'facturacion' ? eurCompact.format : intFmt.format} />
            </div>
            <div className="section">
              <h2>Top agentes{lineFilter !== 'ALL' ? ` · ${lineFilter}` : ''}</h2>
              {agentRanking.rows.length === 0
                ? <div className="empty-hint">Sin datos para este corte.</div>
                : <BarChart rows={agentRanking.rows.map((r) => ({ label: r.name, value: r.value, color: 'var(--accent)' }))}
                    max={agentRanking.max} fmt={metric === 'facturacion' ? eurCompact.format : intFmt.format} />}
            </div>
          </div>

          {/* Detail table */}
          <div className="section ranking">
            <h2>Detalle por agente · {METRIC_TABS.find((m) => m.id === metric)!.label}</h2>
            <div className="ranking-table-wrap">
              <table className="ranking-table">
                <thead>
                  <tr>
                    <th>Agente</th>
                    {SS27_LINES.map((l) => <th key={l} className="num">{l}</th>)}
                    <th className="num">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {[...data].sort((a, b) => agentMetric(b, metric) - agentMetric(a, metric)).map((a) => {
                    const total = metric === 'facturacion' ? a.totalFact : metric === 'clientes' ? a.totalCli : a.totalNew;
                    return (
                      <tr key={a.agentId}>
                        <td className="name" title={a.agentId}>{a.agentName || a.agentId}</td>
                        {SS27_LINES.map((l) => (
                          <td key={l} className={`num ${lineFilter === l ? 'strong' : ''}`}>{a.byLine[l][metric] ? fmt(a.byLine[l][metric]) : '—'}</td>
                        ))}
                        <td className="num strong">{fmt(total)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td className="strong">Total general</td>
                    {SS27_LINES.map((l) => <td key={l} className="num strong">{fmt(colTotal(l))}</td>)}
                    <td className="num strong">{fmt(SS27_LINES.reduce((s, l) => s + colTotal(l), 0))}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Needs */}
          {data.some((a) => a.necesidades) && (
            <div className="section">
              <h2>Necesidades expresadas por los agentes</h2>
              <div className="ss27-needs-list">
                {data.filter((a) => a.necesidades).map((a) => (
                  <div key={a.agentId} className="ss27-need-item">
                    <div className="ss27-need-agent">{a.agentName || a.agentId}</div>
                    <div className="ss27-need-text">{a.necesidades}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function KpiBanner({ label, value, accent }: { label: string; value: string; accent: string }) {
  return (
    <div className="kpi-card" style={{ borderLeft: `3px solid ${accent}` }}>
      <div className="label">{label}</div>
      <div className="value">{value}</div>
    </div>
  );
}

function BarChart({ rows, max, fmt }: {
  rows: { label: string; value: number; color: string }[];
  max: number;
  fmt: (n: number) => string;
}) {
  return (
    <div className="bi-bars">
      {rows.map((r) => (
        <div key={r.label} className="bi-bar-row">
          <span className="bi-bar-label" title={r.label}>{r.label}</span>
          <div className="bi-bar-track">
            <div className="bi-bar-fill" style={{ width: `${Math.max(1, (r.value / max) * 100)}%`, background: r.color }} />
          </div>
          <span className="bi-bar-value">{fmt(r.value)}</span>
        </div>
      ))}
    </div>
  );
}

function groupByAgent(rows: ObjetivoRow[], needs: NecesidadRow[]): AgentData[] {
  const map = new Map<string, AgentData>();
  const ensure = (id: string, name: string): AgentData => {
    let a = map.get(id);
    if (!a) {
      a = {
        agentId: id, agentName: name,
        byLine: SS27_LINES.reduce((acc, l) => { acc[l] = { facturacion: 0, clientes: 0, clientesNuevos: 0 }; return acc; }, {} as Record<SS27Line, LineCell>),
        totalFact: 0, totalCli: 0, totalNew: 0, necesidades: '',
      };
      map.set(id, a);
    }
    return a;
  };
  for (const r of rows) {
    const a = ensure(r.agente_id, r.agente_nombre);
    if ((SS27_LINES as readonly string[]).includes(r.linea)) {
      const fact = Number(r.facturacion_eur) || 0;
      const cli = Number(r.num_clientes) || 0;
      const nue = Number(r.num_clientes_nuevos) || 0;
      a.byLine[r.linea as SS27Line] = { facturacion: fact, clientes: cli, clientesNuevos: nue };
      a.totalFact += fact; a.totalCli += cli; a.totalNew += nue;
    }
  }
  for (const n of needs) ensure(n.agente_id, n.agente_nombre).necesidades = n.necesidades ?? '';
  return Array.from(map.values()).sort((x, y) => y.totalFact - x.totalFact);
}
